import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Button,
  Divider,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { tableCellClasses } from "@mui/material/TableCell";
import IndexHeader from "app/shared-components/IndexHeader";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { dashboardService } from "src/app/services/api/dashboard.service";
import { OverviewProgramData } from "src/app/@types/interfaces/admin-dashboard.interface";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#202441",
    color: theme.palette.common.white,
    fontWeight: "bold",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

export default function ProgramOverview() {
  const [programs, setPrograms] = useState<OverviewProgramData[]>([]);
  const [selections, setSelections] = useState({
    main: "",
    applicationStatus: "",
    jobOpenings: "",
    hostDates: "",
    youthDates: "",
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dashboardService.getProgramOverviewData().then((resp) => {
      if (resp.success && resp.data?.programs) {
        setPrograms(resp.data.programs);

        // Initialize all dropdowns with the first program
        const firstProgram = resp.data.programs[0].name;
        setSelections({
          main: firstProgram,
          applicationStatus: firstProgram,
          jobOpenings: firstProgram,
          hostDates: firstProgram,
          youthDates: firstProgram,
        });
      }
      setLoading(false);
    });
  }, []);

  // Get the selected program for each section
  const programData = useMemo(() => {
    if (programs.length === 0)
      return {
        main: null,
        applicationStatus: null,
        jobOpenings: null,
        hostDates: null,
        youthDates: null,
      };

    return {
      main: programs.find((p) => p.name === selections.main) || programs[0],
      applicationStatus:
        programs.find((p) => p.name === selections.applicationStatus) ||
        programs[0],
      jobOpenings:
        programs.find((p) => p.name === selections.jobOpenings) || programs[0],
      hostDates:
        programs.find((p) => p.name === selections.hostDates) || programs[0],
      youthDates:
        programs.find((p) => p.name === selections.youthDates) || programs[0],
    };
  }, [programs, selections]);

  // Chart configs
  const pieConfig = useMemo(() => {
    const p = programData.applicationStatus;
    if (!p) return { series: [], options: {} as ApexOptions };

    return {
      series: p.applicationStatus.map((s) => s.count),
      options: {
        chart: { type: "pie" as const },
        labels: p.applicationStatus.map((s) => s.status),
        legend: { position: "bottom" as const },
        dataLabels: {
          enabled: true,
          formatter: function (val: number, opts) {
            // Display the actual count instead of percentage
            return opts.w.globals.series[opts.seriesIndex].toString();
          },
        },
        tooltip: {
          y: {
            formatter: function (val: number) {
              return val.toString();
            },
          },
        },
        states: {
          hover: {
            filter: { type: "none" },
          },
          active: {
            filter: { type: "none" },
          },
        },
        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: {
                height: 300,
              },
              legend: {
                position: "bottom",
              },
            },
          },
        ],
      } as ApexOptions,
    };
  }, [programData.applicationStatus]);

  const barConfig = useMemo(() => {
    const p = programData.jobOpenings;
    if (!p) return { series: [], options: {} as ApexOptions };

    return {
      series: [
        { name: "Openings", data: p.categoryJobOpenings.map((c) => c.count) },
      ],
      options: {
        chart: { type: "bar" as const },
        plotOptions: { bar: { horizontal: false } },
        xaxis: {
          categories: p.categoryJobOpenings.map((c) => c.category),
          labels: { rotate: -45 },
        },
        yaxis: { title: { text: "Count" } },
      } as ApexOptions,
    };
  }, [programData.jobOpenings]);

  const handleChange =
    (section: keyof typeof selections) => (e: SelectChangeEvent) => {
      setSelections((prev) => ({
        ...prev,
        [section]: e.target.value,
      }));
    };

  if (loading) {
    return (
      <>
        <IndexHeader
          name="Program Overview"
          update={false}
          add={false}
          redirectingPath="/program-overview"
        />
        <Box
          className="m-24"
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="300px"
        >
          <CircularProgress />
        </Box>
      </>
    );
  }

  if (!programData.main) {
    return (
      <>
        <IndexHeader
          name="Program Overview"
          update={false}
          add={false}
          redirectingPath="/program-overview"
        />
        <Box
          className="m-24"
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="300px"
        >
          <Typography variant="h6" color="error">
            No program data available
          </Typography>
        </Box>
      </>
    );
  }

  const renderProgramSelector = (
    section: keyof typeof selections,
    label: string
  ) => (
    <FormControl size="small" sx={{ minWidth: 200 }}>
      <InputLabel>{label}</InputLabel>
      <Select
        value={selections[section]}
        label={label}
        onChange={handleChange(section)}
      >
        {programs.map((pr) => (
          <MenuItem key={`${section}-${pr.name}`} value={pr.name}>
            {pr.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <>
      <IndexHeader
        name="Program Overview"
        update={false}
        add={false}
        redirectingPath="/program-overview"
      />

      <Box className="m-24">
        {/* Main program selector */}
        <Box mb={3}>{renderProgramSelector("main", "Select Program")}</Box>

        {/* Metrics row */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {programData.main.metrics &&
            Object.entries(programData.main.metrics).map(([key, val]) => (
              <Grid item xs={12} sm={6} md={4} lg={2} key={key}>
                <Button
                  variant="contained"
                  fullWidth
                  sx={{ textTransform: "none", py: 2 }}
                >
                  {`${key
                    .replace(/([A-Z])/g, " $1")
                    .replace(/^./, (str) => str.toUpperCase())} ${val}`}
                </Button>
              </Grid>
            ))}
        </Grid>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Typography variant="h6">Application Status</Typography>
                  {renderProgramSelector("applicationStatus", "Select Program")}
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Chart
                  key={`pie-chart-${selections.applicationStatus}`}
                  options={pieConfig.options}
                  series={pieConfig.series}
                  type="pie"
                  height={300}
                />
                <Box sx={{ mt: 2, textAlign: "center" }}>
                  <Typography variant="body2" color="text.secondary">
                    Total:{" "}
                    {programData.applicationStatus?.applicationStatus.reduce(
                      (sum, item) => sum + item.count,
                      0
                    )}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Typography variant="h6">
                    Categories-wise Job Openings
                  </Typography>
                  {renderProgramSelector("jobOpenings", "Select Program")}
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Chart
                  key={`bar-chart-${selections.jobOpenings}`}
                  options={barConfig.options}
                  series={barConfig.series}
                  type="bar"
                  height={300}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Typography variant="h6">Host Important Dates</Typography>
                  {renderProgramSelector("hostDates", "Select Program")}
                </Box>
                <Divider sx={{ mb: 2 }} />
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <StyledTableCell>Event</StyledTableCell>
                        <StyledTableCell>Date</StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {programData.hostDates?.hostImportantDates &&
                        programData.hostDates.hostImportantDates.map((d) => (
                          <TableRow key={d.label}>
                            <StyledTableCell>{d.label}</StyledTableCell>
                            <StyledTableCell>{d.value}</StyledTableCell>
                          </TableRow>
                        ))}
                      {(!programData.hostDates?.hostImportantDates ||
                        programData.hostDates.hostImportantDates.length ===
                          0) && (
                        <TableRow>
                          <StyledTableCell colSpan={2} align="center">
                            No data available
                          </StyledTableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Typography variant="h6">Youth Important Dates</Typography>
                  {renderProgramSelector("youthDates", "Select Program")}
                </Box>
                <Divider sx={{ mb: 2 }} />
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <StyledTableCell>Event</StyledTableCell>
                        <StyledTableCell>Date</StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {programData.youthDates?.youthImportantDates &&
                        programData.youthDates.youthImportantDates.map((d) => (
                          <TableRow key={d.label}>
                            <StyledTableCell>{d.label}</StyledTableCell>
                            <StyledTableCell>{d.value}</StyledTableCell>
                          </TableRow>
                        ))}
                      {(!programData.youthDates?.youthImportantDates ||
                        programData.youthDates.youthImportantDates.length ===
                          0) && (
                        <TableRow>
                          <StyledTableCell colSpan={2} align="center">
                            No data available
                          </StyledTableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </>
  );
}
